import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  CheckCheck, 
  Info,
  ChevronRight,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import {  VenueNotification } from '../../types/notifications';
import { CATEGORY_ICON_MAP, CATEGORY_STYLE_MAP } from '../../lib/notificationMaps';
import { clsx } from 'clsx';



const formatRelativeTime = (timestamp: string): string => {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { notifications, unreadCount, markAsRead, markAllAsRead, addTestNotification } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'unread') return !n.read;
    return true;
  }).slice(0, 8); // Show up to 8 recent in popover

  const handleNotificationClick = (notif: VenueNotification) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    if (notif.actionUrl) {
      setIsOpen(false);
      navigate(notif.actionUrl);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Header Icon Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="View notifications"
        className={clsx(
          "relative p-2.5 rounded-xl border transition-all duration-200 flex items-center justify-center touch-manipulation",
          isOpen 
            ? "bg-brand-primary/10 border-brand-primary/40 text-brand-primary" 
            : "bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white"
        )}
      >
        <Bell size={19} className={clsx(unreadCount > 0 && "animate-pulse")} />
        
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 bg-gradient-to-r from-brand-primary to-orange-500 text-white font-black text-[10px] rounded-full flex items-center justify-center border-2 border-brand-surface shadow-lg shadow-brand-primary/30">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-brand-surface border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Header */}
          <div className="p-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center text-brand-primary border border-brand-primary/20">
                <Bell size={16} />
              </div>
              <div>
                <h3 className="font-bold text-sm text-white tracking-tight leading-tight flex items-center gap-2">
                  Notifications
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-brand-primary/20 text-brand-primary text-[11px] font-bold">
                      {unreadCount} unread
                    </span>
                  )}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  title="Mark all as read"
                  className="px-2 py-1 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-1"
                >
                  <CheckCheck size={14} />
                  <span className="hidden sm:inline">Read all</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Filter Tabs */}
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between bg-black/20 text-xs">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('all')}
                className={clsx(
                  "px-3 py-1 rounded-lg font-medium transition-colors",
                  activeTab === 'all'
                    ? "bg-white/10 text-white font-bold"
                    : "text-gray-400 hover:text-white"
                )}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setActiveTab('unread')}
                className={clsx(
                  "px-3 py-1 rounded-lg font-medium transition-colors",
                  activeTab === 'unread'
                    ? "bg-white/10 text-white font-bold"
                    : "text-gray-400 hover:text-white"
                )}
              >
                Unread ({unreadCount})
              </button>
            </div>

            <button
              onClick={addTestNotification}
              title="Trigger a test alert"
              className="text-[11px] text-brand-primary hover:underline flex items-center gap-1"
            >
              <Sparkles size={12} />
              + Test Alert
            </button>
          </div>

          {/* Notification Items List */}
          <div className="max-h-80 overflow-y-auto custom-scrollbar divide-y divide-white/5">
            {filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto text-gray-500 mb-3">
                  <Bell size={22} className="opacity-40" />
                </div>
                <p className="text-sm font-semibold text-gray-300">
                  {activeTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {activeTab === 'unread' ? 'You are all caught up!' : 'Activity and alerts will appear here.'}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notif) => {
                const IconComponent = CATEGORY_ICON_MAP[notif.category] || Info;
                const colors = CATEGORY_STYLE_MAP[notif.category] || CATEGORY_STYLE_MAP.system;

                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={clsx(
                      "p-3.5 transition-all flex items-start gap-3 cursor-pointer group hover:bg-white/[0.04]",
                      !notif.read ? "bg-brand-primary/[0.03]" : "opacity-80"
                    )}
                  >
                    {/* Category Icon */}
                    <div className={clsx(
                      "w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border transition-transform group-hover:scale-105",
                      colors.bg,
                      colors.text,
                      colors.border
                    )}>
                      <IconComponent size={18} />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <h4 className={clsx(
                          "text-xs truncate tracking-tight",
                          !notif.read ? "font-bold text-white" : "font-medium text-gray-300"
                        )}>
                          {notif.title}
                        </h4>
                        <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                          {formatRelativeTime(notif.timestamp)}
                        </span>
                      </div>

                      <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                        {notif.message}
                      </p>

                      {notif.actionLabel && (
                        <div className="mt-2 flex items-center gap-1 text-[11px] font-bold text-brand-primary group-hover:underline">
                          <span>{notif.actionLabel}</span>
                          <ChevronRight size={12} />
                        </div>
                      )}
                    </div>

                    {/* Unread Indicator */}
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-brand-primary shrink-0 mt-1.5 shadow-sm shadow-brand-primary/50" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer View All Link */}
          <div className="p-3 border-t border-white/5 bg-white/[0.01] text-center">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/admin/notifications');
              }}
              className="w-full py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-bold text-gray-200 hover:text-white transition-all flex items-center justify-center gap-2 border border-white/5"
            >
              <span>View Full Notification Center</span>
              <ExternalLink size={13} className="text-brand-primary" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
